import { PointCloudLayer } from '@deck.gl/layers';
import { load } from '@loaders.gl/core';
import { LASLoader } from '@loaders.gl/las';
import proj4 from 'proj4';
import bbox from '@turf/bbox';
import { showMapConfirmToast } from "../utils/Toast/mapToast";

proj4.defs(
    'EPSG:3908',
    '+proj=tmerc +lat_0=0 +lon_0=18 +k=0.9999 +x_0=6500000 +y_0=0 +ellps=bessel +towgs84=682,-203,480,0,0,0,0 +units=m +no_defs +type=crs'
);

proj4.defs(
    "EPSG:8678",
    "+proj=tmerc +lat_0=0 +lon_0=18 +k=0.9999 +x_0=6500000 +y_0=0 +ellps=bessel +towgs84=682,-203,480,0,0,0,0 +units=m +no_defs +type=crs"
);

const generateRandomColor = () => [
    Math.floor(Math.random() * 255),
    Math.floor(Math.random() * 255),
    Math.floor(Math.random() * 255),
    255,
];

const transformToWGS84 = (crs, x, y) => {
    if (crs === 'EPSG:4326') {
        return [x, y];
    }

    const [lon, lat] = proj4(crs, 'EPSG:4326', [x, y]);
    return [lon, lat];
};

const findLowestPoint = (positions) => {
    let lowestPoint = null;

    for (let i = 0; i < positions.length; i += 3) {
        const height = positions[i + 2];

        if (lowestPoint === null || height < lowestPoint.height) {
            lowestPoint = {
                longitude: positions[i],
                latitude: positions[i + 1],
                height,
            };
        }
    }

    return lowestPoint;
};

const classifyPoints = (lasData, crs) => {
    const positions = lasData.attributes.POSITION.value;

    const classes =
        lasData.attributes.classification?.value ||
        new Uint8Array(positions.length / 3).fill(0);

    const classColors = new Map();
    const points = new Array(positions.length / 3);

    const lowestPoint = findLowestPoint(positions);

    for (let i = 0, j = 0; i < positions.length; i += 3, j++) {
        const [lon, lat] = transformToWGS84(crs, positions[i], positions[i + 1]);
        const classification = classes[j] ?? 0;

        if (!classColors.has(classification)) {
            classColors.set(classification, generateRandomColor());
        }

        points[j] = {
            position: [
                lon,
                lat,
                positions[i + 2] - lowestPoint.height,
            ],
            color: classColors.get(classification),
            classification,
        };
    }

    return points;
};

const handleFileOpen = async (file, crs) => {
    try {
        if (!file) throw new Error('No file provided');

        if (file.size > 250 * 1024 * 1024) {
            alert('Maksimalna dozvoljena veličina oblaka tačaka je 250 MB.');
            return null;
        }

        const blob = new Blob([await file.arrayBuffer()], { type: '.las' });
        const lasData = await load(blob, LASLoader);

        const points = classifyPoints(lasData, crs);
        return points;
    } catch (error) {
        console.error('Error processing LAS file:', error);
    }
};

export const loadLAS = async (file, crs) => {
    try {
        const las = await handleFileOpen(file, crs);
        return las;
    } catch (error) {
        console.error('Error loading LAS:', error);
        throw error;
    }
};

export const setLASLayer = (
    layerIndex,
    las,
    setHoverInfo,
    setLasLayers,
    setViewState
) => {
    setLasLayers(prev => [
        ...prev,
        new PointCloudLayer({
            id: `las-${layerIndex}`,
            data: las,
            opacity: 1,
            getPosition: (d) => d.position,
            getColor: (d) => d.color,
            pointSize: 0.43,
            pickable: true,
            onHover: (info) => {
                setHoverInfo(info?.object ? info : null);
            },
        }),
    ]);

    if (las && las.length > 0) {
        const coordinates = las.map(p => [p.position[0], p.position[1]]);

        const lasBBox = bbox({
            type: 'FeatureCollection',
            features: coordinates.map(coord => ({
                type: 'Feature',
                geometry: {
                    type: 'Point',
                    coordinates: coord,
                },
                properties: {},
            })),
        });

        const [minX, minY, maxX, maxY] = lasBBox;
        const centerLon = (minX + maxX) / 2;
        const centerLat = (minY + maxY) / 2;
        const lonRange = maxX - minX;
        const latRange = maxY - minY;
        const zoom = Math.log2(360 / Math.max(lonRange, latRange));

        showMapConfirmToast({
            title: "Pozicioniranje mape",
            message: "Da li želite da se prikaz mape pozicionira prema učitanom LAS sloju?",
            confirmText: "Da",
            cancelText: "Ne",
            variant: "info",
            onConfirm: () => {
                setViewState(prev => ({
                    ...prev,
                    longitude: centerLon,
                    latitude: centerLat,
                    zoom,
                    pitch: 0,
                    bearing: 0,
                    transitionDuration: 1000,
                }));
            },
        });
    }
};